// ─── Display Constants ──────────────────────────────────────────────────
// Label, color, and formatting helpers for dashboard display.
// All display mappings are centralized here — never define local maps in pages.

export const INCIDENT_STATUS_LABELS: Record<string, string> = {
	open: 'Open',
	investigating: 'Investigating',
	contained: 'Contained',
	resolved: 'Resolved',
	dismissed: 'Dismissed',
};

export const INCIDENT_STATUS_COLORS: Record<string, string> = {
	open: 'bg-status-open text-white',
	investigating: 'bg-status-investigating text-white',
	contained: 'bg-status-contained text-white',
	resolved: 'bg-status-resolved text-white',
	dismissed: 'bg-status-dismissed text-white',
};

export const EVIDENCE_TIER_LABELS: Record<string, string> = {
	A: 'Tier A — Cryptographic',
	B: 'Tier B — Deterministic',
	C: 'Tier C — Inferred',
};

export const THREAT_CLASS_LABELS: Record<string, string> = {
	T1: 'T1 — Unpinned Action Reference',
	T2: 'T2 — Mutable Tag Resolution',
	T3: 'T3 — Injection via Untrusted Input',
	T4: 'T4 — Excessive Permissions',
	T5: 'T5 — Missing OIDC / Attestation',
	T6: 'T6 — Upstream Dependency Risk',
	T7: 'T7 — Secret Exposure',
	T8: 'T8 — Lateral Movement',
	T9: 'T9 — Supply Chain Attack',
};

export const POLICY_ACTION_LABELS: Record<string, string> = {
	quarantine: 'Quarantine',
	cancel_runs: 'Cancel Runs',
	block: 'Block',
	require_approval: 'Require Approval',
	alert: 'Alert',
	open_pr: 'Open PR',
	observe: 'Observe',
	ignore: 'Ignore',
};

export const POLICY_ACTION_COLORS: Record<string, string> = {
	quarantine: 'bg-red-100 text-red-800',
	cancel_runs: 'bg-red-100 text-red-800',
	block: 'bg-red-100 text-red-800',
	require_approval: 'bg-blue-100 text-blue-800',
	alert: 'bg-yellow-100 text-yellow-800',
	open_pr: 'bg-blue-100 text-blue-800',
	observe: 'bg-gray-100 text-gray-800',
	ignore: 'bg-gray-100 text-gray-800',
};

export const ROLE_LABELS: Record<string, string> = {
	viewer: 'Viewer',
	operator: 'Operator',
	admin: 'Admin',
};

export const TRUST_GRAPH_RISK_LABELS: Record<string, string> = {
	none: 'Stable',
	low: 'Low Risk',
	medium: 'Medium Risk',
	high: 'High Risk',
	critical: 'Critical',
};

export const TRUST_GRAPH_RISK_CLASSES: Record<string, string> = {
	none: 'text-muted-foreground',
	low: 'text-severity-low',
	medium: 'text-severity-medium',
	high: 'text-severity-high',
	critical: 'text-severity-critical',
};

// ─── Attack Path Score Levels ───────────────────────────────────────────────

export const ATTACK_PATH_SCORE_LABELS: Record<string, string> = {
	critical: 'Critical',
	high: 'High',
	medium: 'Medium',
	low: 'Low',
};

export const ATTACK_PATH_SCORE_COLORS: Record<string, string> = {
	critical: 'bg-red-100 text-red-800',
	high: 'bg-orange-100 text-orange-800',
	medium: 'bg-yellow-100 text-yellow-800',
	low: 'bg-green-100 text-green-800',
};

export function getScoreLevel(score: number): 'critical' | 'high' | 'medium' | 'low' {
	if (score >= 7) return 'critical';
	if (score >= 4) return 'high';
	if (score >= 2) return 'medium';
	return 'low';
}

// ─── Analysis Path Labels ───────────────────────────────────────────────────

export const ANALYSIS_PATH_LABELS: Record<string, string> = {
	hot: 'Structural',
	warm: 'Content Analysis',
	cold: 'Baseline',
};

export const ANALYSIS_PATH_COLORS: Record<string, string> = {
	hot: 'bg-blue-100 text-blue-800',
	warm: 'bg-amber-100 text-amber-800',
	cold: 'bg-gray-100 text-gray-800',
};

export const TRUST_GRAPH_TONE_CLASSES: Record<string, string> = {
	neutral: 'border-border bg-muted/60 text-foreground',
	info: 'border-primary/20 bg-primary/10 text-primary',
	success: 'border-green-200 bg-green-50 text-green-700',
	warning: 'border-amber-200 bg-amber-50 text-amber-700',
	danger: 'border-red-200 bg-red-50 text-red-700',
};

export const TRUST_GRAPH_CATEGORY_LABELS: Record<string, string> = {
	'control-plane': 'Control Plane',
	automation: 'Automation',
	execution: 'Execution',
	environment: 'Environment',
	'supply-chain': 'Supply Chain',
	evidence: 'Evidence',
	authority: 'Authority',
};

export const TRUST_GRAPH_NODE_TYPE_LABELS: Record<string, string> = {
	organization: 'Organization',
	repository: 'Repository',
	workflow: 'Workflow',
	workflow_job: 'Job',
	environment: 'Environment',
	access_reference: 'Access Reference',
	secret_exposure_class: 'Exposure Class',
	action: 'Action',
	action_version: 'Action Version',
	container_image: 'Container Image',
	package_dependency: 'Package Dependency',
	package_release: 'Package Release',
	attestation: 'Attestation',
	maintainer: 'Maintainer',
	trusted_publisher: 'Trusted Publisher',
};

export const TRUST_GRAPH_SWATCH_CLASSES: Record<string, string> = {
	'var(--color-primary)': 'bg-[var(--color-primary)]',
	'var(--color-status-contained)': 'bg-[var(--color-status-contained)]',
	'var(--color-status-investigating)': 'bg-[var(--color-status-investigating)]',
	'var(--color-status-resolved)': 'bg-[var(--color-status-resolved)]',
	'var(--color-secondary-foreground)': 'bg-[var(--color-secondary-foreground)]',
	'var(--color-severity-medium)': 'bg-[var(--color-severity-medium)]',
	'var(--color-muted-foreground)': 'bg-[var(--color-muted-foreground)]',
	'var(--color-severity-high)': 'bg-[var(--color-severity-high)]',
	'var(--color-severity-critical)': 'bg-[var(--color-severity-critical)]',
};

export function formatTimestamp(unix: number): string {
	return new Date(unix * 1000).toLocaleString();
}

export function formatRelativeTime(unix: number): string {
	const now = Date.now() / 1000;
	const diff = now - unix;

	if (diff < 60) return 'just now';
	if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
	if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
	return `${Math.floor(diff / 86400)}d ago`;
}

// ─── Sync Constants ─────────────────────────────────────────────────────

export const SYNC_STATUS_LABELS: Record<string, string> = {
	idle: 'Not synced',
	running: 'Syncing...',
	completed: 'Synced',
	failed: 'Sync failed',
};

export const SYNC_STATUS_COLORS: Record<string, string> = {
	idle: 'text-muted-foreground',
	running: 'text-blue-500',
	completed: 'text-green-600',
	failed: 'text-red-600',
};

export const SYNC_INTERVAL_OPTIONS = [
	{ label: 'Every hour', value: 3600 },
	{ label: 'Every 6 hours', value: 21600 },
	{ label: 'Every 12 hours', value: 43200 },
	{ label: 'Every 24 hours', value: 86400 },
	{ label: 'Every 3 days', value: 259200 },
	{ label: 'Every 7 days', value: 604800 },
] as const;
